import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('vrs_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // [Phase 3(c) Stage 1, §8] Surface an explicit "session expired" state
      // instead of a silent bounce. Only flag it when a session actually
      // existed (so a 401 on the login attempt itself isn't mislabelled), and
      // don't redirect when already on /login (which would reload the page and
      // wipe the login error message).
      const hadSession = !!localStorage.getItem('vrs_token');
      localStorage.removeItem('vrs_token');
      localStorage.removeItem('vrs_user');
      if (hadSession) {
        sessionStorage.setItem('vrs_session_expired', '1');
      }
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  verifyEmail: (email, code) => api.post('/auth/verify-email', { email, code }),
  resendCode: (email) => api.post('/auth/resend-code', { email }),
  setRole: (role) => api.post('/auth/set-role', { role }),
  login: (email, password) => api.post('/auth/login', { email, password }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (email, code, new_password) => api.post('/auth/reset-password', { email, code, new_password }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

export const dashboardAPI = { getStats: () => api.get('/dashboard/stats') };

export const scansAPI = {
  getRecent: (limit = 10) => api.get('/scans/recent', { params: { limit } }),
  list: (limit = 50, skip = 0) => api.get('/scans', { params: { limit, skip } }),
  get: (scanId) => api.get(`/scans/${scanId}`),
};

export const lawsAPI = {
  list: (limit = 50, skip = 0) => api.get('/laws', { params: { limit, skip } }),
  get: (lawId) => api.get(`/laws/${lawId}`),
};

export const categoriesAPI = { list: () => api.get('/categories') };
export const subscriptionsAPI = { getMine: () => api.get('/subscriptions/me') };
export const usersAPI = { updateProfile: (data) => api.put('/users/me', data) };

// [Phase 3(c) Sub-stage 2] Ley 40 applicability-gate client. GET returns the five
// Art. 2 questions (question_en + verbatim_es + order), the user's current answers
// (three-state: true / false / null=unanswered), and the derived evaluation. PUT
// submits { answers: { condition_id: bool } } and recomputes/records scope server-side.
export const ley40GateAPI = {
  get: () => api.get('/users/me/ley40-gate'),
  update: (answers) => api.put('/users/me/ley40-gate', { answers }),
};

// Admin API
export const adminAPI = {
  listUsers: (params) => api.get('/admin/users', { params }),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (userId, data) => api.patch(`/admin/users/${userId}`, data),
  resetUserPassword: (userId, data) => api.post(`/admin/users/${userId}/reset-password`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getUserActivity: (userId, limit = 50) => api.get(`/admin/users/${userId}/activity`, { params: { limit } }),
  getActivity: (params) => api.get('/admin/activity', { params }),
  getUsageSummary: () => api.get('/admin/usage/summary'),
  getScansPerDay: (days = 30) => api.get('/admin/usage/scans-per-day', { params: { days } }),
  getUsersByRole: () => api.get('/admin/usage/users-by-role'),
  getTopDomains: () => api.get('/admin/usage/top-domains'),
  listExemptions: () => api.get('/admin/exemptions'),
  upsertExemption: (data) => api.post('/admin/exemptions', data),
  deleteExemption: (entityUserId) => api.delete(`/admin/exemptions/${entityUserId}`),
  listSettings: () => api.get('/admin/settings'),
  updateSetting: (key, value) => api.put('/admin/settings', { key, value }),
  // [Phase 3(c) Stage 2] gate-profile editing, scan grants, admin audit log
  getGateProfile: (userId) => api.get(`/admin/users/${userId}/gate-profile`),
  updateGateProfile: (userId, data) => api.patch(`/admin/users/${userId}/gate-profile`, data),
  grantScan: (userId, data) => api.post(`/admin/users/${userId}/scan-grant`, data),
  getAuditLog: (params) => api.get('/admin/audit-log', { params }),
};

// Legal Entry API
export const legalAPI = {
  // Categories
  listCategories: (includeArchived = false) => api.get('/legal/categories', { params: { include_archived: includeArchived } }),
  createCategory: (data) => api.post('/legal/categories', data),
  updateCategory: (categoryId, data) => api.put(`/legal/categories/${categoryId}`, data),
  archiveCategory: (categoryId) => api.patch(`/legal/categories/${categoryId}/archive`),
  listPublicCategories: () => api.get('/legal/categories/public'),
  // Laws
  listLaws: (params) => api.get('/legal/laws', { params }),
  getLaw: (lawId) => api.get(`/legal/laws/${lawId}`),
  createLaw: (data) => api.post('/legal/laws', data),
  updateLaw: (lawId, data) => api.put(`/legal/laws/${lawId}`, data),
  approveLaw: (lawId) => api.post(`/legal/laws/${lawId}/approve`),
  archiveLaw: (lawId) => api.post(`/legal/laws/${lawId}/archive`),
  uploadPdf: (lawId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/legal/laws/${lawId}/upload-pdf`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  listPublicLaws: (params) => api.get('/legal/laws/public', { params }),
  translateLaw: (lawId) => api.post(`/legal/laws/${lawId}/translate`),
  verifyTranslation: (lawId) => api.post(`/legal/laws/${lawId}/verify-translation`),
  resetTranslation: (lawId) => api.post(`/legal/laws/${lawId}/reset-translation`),
  listSectors: (includeUniversal = true) => api.get('/legal/sectors', { params: { include_universal: includeUniversal } }),
  listThemes: () => api.get('/legal/themes'),
  deleteLaw: (lawId) => api.delete(`/legal/laws/${lawId}`),
};

// Billing API
export const billingAPI = {
  getStatus: () => api.get('/billing/status'),
  getConfig: () => api.get('/billing/config'),
  createCheckout: (plan) => api.post('/billing/checkout', { plan }),
  createPortal: () => api.post('/billing/portal'),
  canScan: () => api.get('/billing/can-scan'),
};

// Fines API
export const finesAPI = {
  getFineReport: (scanId) => api.get(`/fines/report/${scanId}`),
  create: (data) => api.post('/fines', data),
  list: (params) => api.get('/fines', { params }),
  get: (fineId) => api.get(`/fines/${fineId}`),
  update: (fineId, data) => api.patch(`/fines/${fineId}`, data),
  getEntityFines: (userId) => api.get(`/fines/entity/${userId}`),
};

export default api;
